import { useState } from 'react';
import type { DrawerEvent as LedgerDrawerEvent } from '../../../utils/drawerLedger';

type DrawerEvent = LedgerDrawerEvent;

interface SessionFields {
  openingCash: number;
  salesAmount: number;
  cashIn: number;
  cashOut: number;
  receivedCash: number;
  receivedBankAmount: number;
  salesReturnAmount: number;
  salesRefundAmount: number;
  adjustedCreditAmount: number;
}

interface ApiMovement {
  id: number;
  movementType: string;
  amount: number;
  reason: string;
  note: string;
  createdAt: string;
}

interface DrawerDetailsModalProps {
  onClose: () => void;
  drawerInfo: {
    store: string;
    lane: string;
    cashier: string;
    shift: string;
    status: 'ready' | 'open' | 'error' | 'locked';
    openingBalance: number;
    cashSales: number;
    cashRefunds: number;
    paidIn: number;
    paidOut: number;
    expectedBalance: number;
  };
  events: DrawerEvent[];
  apiEvents?: ApiMovement[];
  sessionFields?: SessionFields;
  isLoading?: boolean;
}

export default function DrawerDetailsModal({ onClose, drawerInfo, events, apiEvents, sessionFields, isLoading = false }: DrawerDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'events'>('summary');

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'opening_balance': return 'ri-wallet-3-line text-blue-600';
      case 'cash_sale': return 'ri-money-dollar-circle-line text-green-600';
      case 'cash_refund': return 'ri-arrow-go-back-line text-red-600';
      case 'manual_open': return 'ri-hand-coin-line text-orange-600';
      case 'no_sale': return 'ri-cash-line text-gray-600';
      case 'paid_in': return 'ri-arrow-down-circle-line text-green-600';
      case 'paid_out': return 'ri-arrow-up-circle-line text-red-600';
      case 'reconciliation': return 'ri-calculator-line text-purple-600';
      default: return 'ri-information-line text-gray-600';
    }
  };

  const getEventLabel = (type: string) => {
    switch (type) {
      case 'opening_balance': return 'Opening Balance';
      case 'cash_sale': return 'Cash Sale';
      case 'cash_refund': return 'Cash Refund';
      case 'manual_open': return 'Manual Open';
      case 'no_sale': return 'No Sale';
      case 'paid_in': return 'Paid In';
      case 'paid_out': return 'Paid Out';
      case 'reconciliation': return 'Reconciliation';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[520px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-safe-2-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">Cash Drawer Details</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="border-b">
          <div className="flex gap-1 p-2">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'summary'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-dashboard-line mr-2"></i>
              Summary
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'events'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              Event Log ({apiEvents && apiEvents.length > 0 ? apiEvents.length : events.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'summary' && (
            <div className="grid grid-cols-2 gap-4">
              {/* Left column: info + balance */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                      <p className="text-xs text-gray-500">Outlet</p>
                      <p className="text-xs font-semibold text-gray-900 truncate">{drawerInfo.store}</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                      <p className="text-xs text-gray-500">Counter</p>
                      <p className="text-xs font-semibold text-gray-900 truncate">{drawerInfo.lane}</p>
                    </div>
                  </div>
                  <div className="w-1/2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                    <p className="text-xs text-gray-500">Cashier</p>
                    <p className="text-xs font-semibold text-gray-900 truncate">{drawerInfo.cashier}</p>
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : sessionFields ? (
                  (() => {
                    const closingCash =
                      sessionFields.openingCash +
                      sessionFields.salesAmount +
                      sessionFields.cashIn -
                      sessionFields.cashOut -
                      sessionFields.receivedBankAmount -
                      sessionFields.salesReturnAmount;
                    return (
                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-4">
                        <p className="text-xs text-teal-700 mb-1">Expected Closing Cash</p>
                        <p className="text-3xl font-bold text-teal-900">{closingCash.toFixed(2)}</p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-4">
                    <p className="text-xs text-teal-700 mb-1">Expected Cash Balance</p>
                    <p className="text-3xl font-bold text-teal-900">{drawerInfo.expectedBalance.toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Right column: session fields breakdown */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full py-6">
                    <i className="ri-loader-4-line animate-spin text-xl text-teal-600"></i>
                  </div>
                ) : sessionFields ? (
                  (() => {
                    const closingCash =
                      sessionFields.openingCash +
                      sessionFields.salesAmount +
                      sessionFields.cashIn -
                      sessionFields.cashOut -
                      sessionFields.receivedBankAmount -
                      sessionFields.salesReturnAmount;
                    return (
                      <div className="space-y-1">
                        <h3 className="text-xs font-semibold text-gray-700 mb-2">Session Summary</h3>
                        {[
                          { label: 'Opening Cash', value: sessionFields.openingCash, color: 'text-gray-900' },
                          { label: '+ Sales', value: sessionFields.salesAmount, color: 'text-green-600' },
                          // Cash In / Cash Out line hidden for now — may be revisited in the future.
                          // { label: '+ Cash In', value: sessionFields.cashIn, color: 'text-green-600' },
                          // { label: '- Cash Out', value: sessionFields.cashOut, color: 'text-red-600' },
                          { label: 'Cash Amount', value: sessionFields.receivedCash, color: 'text-gray-900' },
                          { label: '- Bank/Mobile', value: sessionFields.receivedBankAmount, color: 'text-red-600' },
                          { label: '- Sales Return', value: sessionFields.salesReturnAmount, color: 'text-red-600' },
                          { label: '- Refund', value: sessionFields.salesRefundAmount, color: 'text-red-600' },
                          { label: 'Credit Applied', value: sessionFields.adjustedCreditAmount, color: 'text-gray-900' },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="flex justify-between items-center py-0.5 border-b border-gray-50">
                            <span className={`text-xs ${color}`}>{label}</span>
                            <span className={`text-xs font-medium ${color}`}>{value.toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                          <span className="text-xs font-semibold text-gray-900">Closing Cash</span>
                          <span className="text-sm font-bold text-gray-900">{closingCash.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="space-y-1">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2">Cash Movement</h3>
                    {[
                      { label: 'Opening Balance', value: drawerInfo.openingBalance, color: 'text-gray-900' },
                      { label: '+ Cash Sales', value: drawerInfo.cashSales, color: 'text-green-600' },
                      { label: '- Cash Refunds', value: drawerInfo.cashRefunds, color: 'text-red-600' },
                      // Paid In / Paid Out line hidden for now — may be revisited in the future.
                      // { label: '+ Paid In', value: drawerInfo.paidIn, color: 'text-green-600' },
                      // { label: '- Paid Out', value: drawerInfo.paidOut, color: 'text-red-600' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="flex justify-between items-center py-0.5 border-b border-gray-50">
                        <span className={`text-xs ${color}`}>{label}</span>
                        <span className={`text-xs font-medium ${color}`}>{value.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                      <span className="text-xs font-semibold text-gray-900">Expected Balance</span>
                      <span className="text-sm font-bold text-gray-900">{drawerInfo.expectedBalance.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
                  <span className="text-sm text-gray-500">Loading events...</span>
                </div>
              ) : /* Cash In / Cash Out (apiEvents) event list hidden for now — may be revisited in the future.
                apiEvents && apiEvents.length > 0 ? (
                apiEvents.map((movement) => {
                  const isCashIn = String(movement.movementType).toLowerCase().includes('in') || movement.movementType === '0' || movement.movementType === 0;
                  const icon = isCashIn ? 'ri-arrow-down-circle-line text-green-600' : 'ri-arrow-up-circle-line text-red-600';
                  const label = isCashIn ? 'Cash In' : 'Cash Out';
                  const amountColor = isCashIn ? 'text-green-600' : 'text-red-600';
                  const amountPrefix = isCashIn ? '+' : '-';
                  const dateStr = movement.createdAt
                    ? new Date(movement.createdAt).toLocaleString()
                    : '';
                  return (
                    <div key={movement.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full flex-shrink-0">
                          <i className={`${icon} text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{label}</p>
                              <p className="text-xs text-gray-500">{dateStr}</p>
                            </div>
                            <span className={`text-lg font-bold ${amountColor}`}>
                              {amountPrefix}{Math.abs(Number(movement.amount)).toFixed(2)}
                            </span>
                          </div>
                          {movement.reason && (
                            <div className="bg-gray-50 rounded px-2 py-1 mb-1">
                              <p className="text-xs text-gray-700">
                                <span className="font-medium">Reason:</span> {movement.reason}
                              </p>
                            </div>
                          )}
                          {movement.note && (
                            <div className="bg-blue-50 rounded px-2 py-1">
                              <p className="text-xs text-blue-700">
                                <span className="font-medium">Note:</span> {movement.note}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : */ events.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-history-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-sm text-gray-500">No events recorded yet</p>
                </div>
              ) : (
                events.map((event) => (
                  <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full flex-shrink-0">
                        <i className={`${getEventIcon(event.type)} text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{getEventLabel(event.type)}</p>
                            <p className="text-xs text-gray-500">{event.timestamp}</p>
                          </div>
                          {event.amount !== undefined && (
                            <span className={`text-lg font-bold ${event.type === 'paid_in' || event.type === 'cash_sale' ? 'text-green-600' : event.type === 'paid_out' || event.type === 'cash_refund' ? 'text-red-600' : 'text-gray-900'}`}>
                              {event.type === 'paid_in' || event.type === 'cash_sale' ? '+' : event.type === 'paid_out' || event.type === 'cash_refund' ? '-' : ''}{Math.abs(event.amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                          <span><i className="ri-user-line mr-1"></i>{event.user}</span>
                          <span><i className="ri-shield-user-line mr-1"></i>{event.role}</span>
                          {event.transactionId && <span><i className="ri-barcode-line mr-1"></i>{event.transactionId}</span>}
                        </div>
                        {event.reason && (
                          <div className="bg-gray-50 rounded px-2 py-1 mb-1">
                            <p className="text-xs text-gray-700"><span className="font-medium">Reason:</span> {event.reason}</p>
                          </div>
                        )}
                        {event.note && (
                          <div className="bg-blue-50 rounded px-2 py-1">
                            <p className="text-xs text-blue-700"><span className="font-medium">Note:</span> {event.note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

